import { useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { Text } from '@antimatterhq/ui/components/typography';
import { AxiosError } from 'axios';
import Spacer from '@antimatterhq/ui/components/spacer';
import { JsonText } from '@antimatterhq/ui/components/jsonText';
import Table, { useTableContext } from '@antimatterhq/ui/components/table';
import { useTableColumns } from '@antimatterhq/ui/hooks/table/useTableColumns';
import {
  CustomCellProps,
  useTableRows,
} from '@antimatterhq/ui/hooks/table/useTableRows';
import { TableColumnDefinition } from '@antimatterhq/ui/types/TableColumnDefinition';
import {
  SimpleTableBody,
  SimpleTableSkeletonRows,
  SimpleTable,
} from '@antimatterhq/ui/components/simpleTable';
import { Datasource_TransformStage } from '@/types/api';
import { ResponseError } from '@/components/responseError/ResponseError';
import { exhaustiveMatchingGuard } from '@/utils/util';

type SourceTableType = 'Bronze' | 'SilverPreTransform' | 'SilverTransform';
type PreviewTableType = 'SilverPreTransform' | 'SilverTransform' | 'Gold';

export type DataSourcesDataPreviewDialogProps = {
  isOpen: boolean;
  sourceTable: Datasource_TransformStage | undefined;
  previewTable: Datasource_TransformStage | undefined;
  onClose: () => void;
  sourceTableStage: SourceTableType;
  previewTableStage: PreviewTableType;
  sourceTableIsLoading?: boolean;
  previewTableIsLoading?: boolean;
  sourceTableResponseError?: string;
  previewTableResponseError?: string;
  onRetry?: () => void;
};

type DataPreviewTableCellProps = {
  children: string;
};

// Define a proper data structure for table rows
type DataPreviewTableRowData = {
  id: string;
  [columnId: string]: string;
};

function tryParseJSONObject(jsonString: string): JSON | undefined {
  // Handle empty or special strings
  if (!jsonString || jsonString === 'null' || jsonString === 'undefined') {
    return undefined;
  }

  // Skip number strings
  if (!isNaN(Number(jsonString))) {
    return undefined;
  }

  try {
    const result = JSON.parse(jsonString);
    return result;
  } catch {
    return undefined;
  }
}

const DataPreviewTableCell = ({ children }: DataPreviewTableCellProps) => {
  const json = useMemo(() => tryParseJSONObject(children), [children]);

  if (json !== undefined) {
    return (
      <div className="max-h-48 w-full overflow-y-auto custom-scrollbar">
        <JsonText data={json} />
      </div>
    );
  }

  let output = children;
  if (output === '') {
    output = '-';
  }

  return <Text className="w-full truncate">{output}</Text>;
};

type DataPreviewTableProps = {
  table: Datasource_TransformStage | undefined;
  title: string | JSX.Element;
  isLoading: boolean;
  responseError: AxiosError<{ message: string }> | string | undefined;
  'data-testid': string;
};

// Table content component
const DataPreviewTableContent = ({
  table,
  title,
  'data-testid': dataTestId,
}: DataPreviewTableProps) => {
  const { columnDefinitions } = useTableContext();

  // Transform data for the table rows with proper structure
  const tableData = useMemo((): DataPreviewTableRowData[] => {
    if (!table) return [];

    return table.data.map((row, index) => ({ id: `row-${index}`, ...row }));
  }, [table]);

  // Manage columns
  const {
    columns,
    columnWidths,
    columnVisibility,
    pinnedColumns,
    columnOrders,
    toggleColumnVisibility,
    resetAllColumns,
    onColumnOrderChange,
  } = useTableColumns({
    persistState: false,
  });

  const customBodyCells: Record<
    string,
    | React.ComponentType<CustomCellProps<DataPreviewTableRowData>>
    | React.ReactNode
  > = useMemo(() => {
    return Object.fromEntries(
      columnDefinitions.map((c) => [
        c.id,
        ({ item, column }) => (
          <DataPreviewTableCell>{item[column.id] || ''}</DataPreviewTableCell>
        ),
      ]),
    );
  }, [columnDefinitions]);

  // Generate table rows
  const { headerRow, bodyRows } = useTableRows({
    items: tableData,
    columns,
    customBodyCells,
    'data-testid': dataTestId,
    pageSize: tableData.length ?? 6,
    expandable: true,
  });

  return (
    <>
      <div className="flex flex-row items-center justify-between">
        <Text size="lg" bold style={{ lineHeight: '0.5' }}>
          {title}
        </Text>

        <Table.ColumnManager
          pinnedColumns={pinnedColumns}
          columnOrders={columnOrders}
          columnVisibility={columnVisibility}
          toggleColumnVisibility={toggleColumnVisibility}
          resetColumns={resetAllColumns}
          onColumnOrderChange={onColumnOrderChange}
          data-testid="detection-rules-column-manager"
        />
      </div>

      <Table.ResponsiveContainer containerClassName="h-fit">
        <Table.StyleProvider
          virtualized={false}
          pageSize={undefined}
          columnWidths={columnWidths}
          columnVisibility={columnVisibility}
        >
          <Table.Table>
            <Table.Header>{headerRow}</Table.Header>
            <Table.Body>{bodyRows}</Table.Body>
          </Table.Table>
        </Table.StyleProvider>
      </Table.ResponsiveContainer>
    </>
  );
};

function getDataPreviewTableContentInfo(
  data:
    | {
        [key: string]: string;
      }[]
    | undefined,
  column: string | null | undefined,
) {
  if (!data) {
    return undefined;
  }

  if (!column) {
    return undefined;
  }

  const hasLongContent = data.some((row) => {
    return row[column] && row[column].length > 100;
  });

  const averageContentLength =
    data.reduce((acc, row) => {
      return acc + (row[column]?.length || 0);
    }, 0) / data.length;

  const longestContentLength = data.reduce((acc, row) => {
    return row[column]?.length > acc ? row[column]?.length : acc;
  }, 0);

  return { hasLongContent, averageContentLength, longestContentLength };
}

const getRecommendedContentWidth = (
  hasLongContent: boolean | undefined,
  averageContentLength: number | undefined,
  longestContentLength: number | undefined,
) => {
  if (
    hasLongContent === undefined ||
    averageContentLength === undefined ||
    longestContentLength === undefined
  ) {
    return 250;
  }

  if (hasLongContent) {
    if (averageContentLength > 100) return 750;
    return 500;
  }

  return 250;
};

const DataPreviewTable = (props: DataPreviewTableProps) => {
  const { table, isLoading, responseError } = props;

  const columnDefinitions: TableColumnDefinition[] | undefined = useMemo(() => {
    if (!table || !table.columns) return undefined;

    return table.columns.map((column) => {
      const contentInfo = getDataPreviewTableContentInfo(
        table.data,
        column.name,
      );

      return {
        id: column.name || '',
        title: column.name || '',
        width: getRecommendedContentWidth(
          contentInfo?.hasLongContent,
          contentInfo?.averageContentLength,
          contentInfo?.longestContentLength,
        ),
        enableSorting: false,
        enableFiltering: false,
        tooltip: false,
      };
    });
  }, [table]);

  if (responseError) {
    return (
      <>
        <Text size="lg" bold style={{ lineHeight: '0.5' }}>
          {props.title}
        </Text>
        <div className="h-full w-full flex-col items-center justify-center gap-4 overflow-x-auto overflow-y-auto font-mono custom-scrollbar">
          <ResponseError error={responseError} justify="start" />
        </div>
      </>
    );
  }

  if (isLoading || !columnDefinitions) {
    return (
      <>
        <Text size="lg" bold style={{ lineHeight: '0.5' }}>
          {props.title}
        </Text>
        <SimpleTable>
          <SimpleTableBody>
            <SimpleTableSkeletonRows
              cols={1}
              rows={6}
              height={35}
              data-testid={`${props['data-testid']}-skeleton-rows`}
            />
          </SimpleTableBody>
        </SimpleTable>
      </>
    );
  }

  return (
    <Table.Provider
      columnDefinitions={columnDefinitions}
      localStorageKeyPrefix={`data-preview-table-${props['data-testid']}`}
    >
      <DataPreviewTableContent {...props} />
    </Table.Provider>
  );
};

export const DataSourcesDataPreviewDialog = ({
  isOpen,
  sourceTable,
  previewTable,
  onClose,
  sourceTableStage,
  previewTableStage,
  sourceTableIsLoading,
  previewTableIsLoading,
  sourceTableResponseError,
  previewTableResponseError,
}: DataSourcesDataPreviewDialogProps) => {
  const title = useMemo(() => {
    let result = '';
    switch (sourceTableStage) {
      case 'Bronze':
        result += 'Bronze';
        break;
      case 'SilverPreTransform':
        result += 'Silver Pre-transform';
        break;
      case 'SilverTransform':
        result += 'Silver';
        break;
      default:
        exhaustiveMatchingGuard(sourceTableStage);
    }

    result += ' & ';

    switch (previewTableStage) {
      case 'SilverPreTransform':
        result += 'Silver Pre-transform';
        break;
      case 'SilverTransform':
        result += 'Silver';
        break;
      case 'Gold':
        result += 'Gold';
        break;
      default:
        exhaustiveMatchingGuard(previewTableStage);
    }

    result += ' comparison';
    return result;
  }, [sourceTableStage, previewTableStage]);

  const getTableTitle = useCallback(
    (stage: SourceTableType | PreviewTableType) => {
      switch (stage) {
        case 'Bronze':
          return <span className="bg-bronze">Bronze</span>;
        case 'SilverPreTransform':
          return <span className="bg-silver">Silver pre-transform</span>;
        case 'SilverTransform':
          return <span className="bg-silver">Silver</span>;
        case 'Gold':
          return <span className="bg-gold">Gold</span>;
        default:
          exhaustiveMatchingGuard(stage);
      }
    },
    [],
  );

  const currentTitle = getTableTitle(sourceTableStage);
  const previewTitle = getTableTitle(previewTableStage);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex h-[90vh] max-h-[90vh] w-[90vw] max-w-[90vw] flex-col"
        data-testid="data-preview-dialog"
      >
        <div className="flex h-full flex-col gap-4">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Data preview</DialogTitle>
          </DialogHeader>

          <DialogDescription>
            <Text bold>{title}</Text>
          </DialogDescription>
          <DataPreviewTable
            table={previewTable}
            isLoading={previewTableIsLoading ?? false}
            title={previewTitle || ''}
            data-testid="preview-table"
            responseError={previewTableResponseError}
          />

          {/* If the source table and preview table have the same error, don't show the source table. The error will be shown in the preview table. */}
          {previewTableResponseError &&
          sourceTableResponseError &&
          previewTableResponseError === sourceTableResponseError ? null : (
            <>
              <Spacer size="lg" />

              <DataPreviewTable
                table={sourceTable}
                isLoading={sourceTableIsLoading ?? false}
                title={currentTitle || ''}
                data-testid="source-table"
                responseError={sourceTableResponseError}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
